/** 固定比例裁剪：尺寸检测与 canvas 导出 */

export const PRODUCT_CAROUSEL_ASPECT = 3 / 4;
export const PRODUCT_CAROUSEL_ASPECT_LABEL = '3:4';
/** 素材图（materialImgUrl）平台要求正方形 */
export const PRODUCT_MATERIAL_ASPECT = 1;
export const PRODUCT_MATERIAL_ASPECT_LABEL = '1:1';
/** 比例容差（浮点像素取整） */
export const ASPECT_RATIO_TOLERANCE = 0.02;

export function isAspectRatioMatch(width, height, aspect = PRODUCT_CAROUSEL_ASPECT, tolerance = ASPECT_RATIO_TOLERANCE) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (w <= 0 || h <= 0 || !(aspect > 0)) return false;
  return Math.abs(w / h - aspect) <= tolerance;
}

export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('文件读取失败'));
    reader.readAsDataURL(file);
  });
}

export function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('图片加载失败'));
    img.src = src;
  });
}

export async function readImageNaturalSize(fileOrUrl) {
  let src = '';
  let revoke = null;
  if (typeof fileOrUrl === 'string') {
    src = fileOrUrl;
  } else if (fileOrUrl instanceof Blob) {
    src = URL.createObjectURL(fileOrUrl);
    revoke = src;
  } else {
    throw new Error('无法读取图片');
  }
  try {
    const img = await loadImageElement(src);
    return {
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
    };
  } finally {
    if (revoke) URL.revokeObjectURL(revoke);
  }
}

/**
 * 在原图像素坐标系下，计算最大内接固定比例裁剪框（居中）。
 * @returns {{ sx: number, sy: number, sw: number, sh: number }}
 */
export function computeMaxCenteredCropBox(width, height, aspect = PRODUCT_CAROUSEL_ASPECT) {
  const w = Math.max(1, Number(width) || 0);
  const h = Math.max(1, Number(height) || 0);
  const target = aspect > 0 ? aspect : PRODUCT_CAROUSEL_ASPECT;
  const current = w / h;
  let sw;
  let sh;
  if (current > target) {
    sh = h;
    sw = Math.max(1, Math.round(h * target));
  } else {
    sw = w;
    sh = Math.max(1, Math.round(w / target));
  }
  const sx = Math.max(0, Math.floor((w - sw) / 2));
  const sy = Math.max(0, Math.floor((h - sh) / 2));
  return { sx, sy, sw, sh };
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mime, quality);
  });
}

/**
 * 按像素框裁切并导出 JPEG File。
 * @param {HTMLImageElement|string} imageOrSrc
 * @param {{ sx: number, sy: number, sw: number, sh: number }} box 原图像素坐标
 * @param {{ fileName?: string, maxEdge?: number, maxBytes?: number, outputWidth?: number, outputHeight?: number }} [opts]
 */
export async function exportCroppedImageFile(imageOrSrc, box, opts = {}) {
  const img = typeof imageOrSrc === 'string'
    ? await loadImageElement(imageOrSrc)
    : imageOrSrc;
  const naturalW = img.naturalWidth || img.width;
  const naturalH = img.naturalHeight || img.height;
  const sx = Math.max(0, Math.min(naturalW - 1, Math.round(Number(box?.sx) || 0)));
  const sy = Math.max(0, Math.min(naturalH - 1, Math.round(Number(box?.sy) || 0)));
  const sw = Math.max(1, Math.min(naturalW - sx, Math.round(Number(box?.sw) || 0)));
  const sh = Math.max(1, Math.min(naturalH - sy, Math.round(Number(box?.sh) || 0)));

  let outW = Math.round(Number(opts.outputWidth) || 0);
  let outH = Math.round(Number(opts.outputHeight) || 0);
  if (!(outW > 0 && outH > 0)) {
    outW = sw;
    outH = sh;
    const maxEdge = Number(opts.maxEdge) || 0;
    if (maxEdge > 0 && Math.max(outW, outH) > maxEdge) {
      const scale = maxEdge / Math.max(outW, outH);
      outW = Math.max(1, Math.round(outW * scale));
      outH = Math.max(1, Math.round(outH * scale));
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法裁剪图片');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outW, outH);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);

  const baseName = String(opts.fileName || 'crop').replace(/\.[^.]+$/, '') || 'crop';
  const maxBytes = Number(opts.maxBytes) > 0 ? Number(opts.maxBytes) : 0;
  let quality = 0.92;
  let best = null;
  while (quality >= 0.35) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob) {
      best = blob;
      if (!maxBytes || blob.size <= maxBytes) {
        return new File([blob], `${baseName}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
      }
    }
    quality -= 0.07;
  }
  if (best) {
    return new File([best], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  }
  throw new Error('裁剪导出失败');
}
